/**
 * Edit Template Page
 * Full page for editing existing templates
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BaseLayout } from '@/layouts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  ArrowLeft, 
  Plus, 
  X, 
  GripVertical, 
  Save,
  Loader2,
  FolderOpen,
  Trash2,
} from 'lucide-react';
import { useTemplates } from '@/hooks/useTemplates';
import { AddSectionModal } from '@/components/be-study/modals/AddSectionModal';
import { COUNTRIES } from '@/constants/beStudyMockData';
import { ROUTES } from '@/constants/routes';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

/**
 * Editable Section Item
 */
function EditableSectionItem({ section, index, onRemove, onUpdate }) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div 
      className={cn(
        "rounded-lg border bg-card transition-all",
        isExpanded && "ring-2 ring-primary/20"
      )}
      data-testid={`section-item-${index}`}
    >
      <div className="flex items-center gap-3 p-4">
        <GripVertical className="h-5 w-5 text-muted-foreground cursor-grab shrink-0" />
        <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-medium shrink-0">
          {index + 1}
        </div>
        <div 
          className="flex-1 min-w-0 cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <p className="font-medium truncate">{section.title || 'Untitled Section'}</p>
          {!isExpanded && section.description && (
            <p className="text-xs text-muted-foreground truncate">{section.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {section.required ? (
            <Badge variant="default" className="text-xs">Required</Badge>
          ) : (
            <Badge variant="outline" className="text-xs">Optional</Badge>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-red-500"
            onClick={() => onRemove(index)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {isExpanded && (
        <div className="px-4 pb-4 pt-0 space-y-4 border-t mt-2">
          <div className="pt-4 space-y-4">
            <div className="space-y-2">
              <Label>Section Title</Label>
              <Input
                value={section.title}
                onChange={(e) => onUpdate(index, 'title', e.target.value)}
                placeholder="Enter section title"
                data-testid={`section-title-input-${index}`}
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={section.description || ''}
                onChange={(e) => onUpdate(index, 'description', e.target.value)}
                placeholder="Enter section description"
                rows={2}
                data-testid={`section-description-input-${index}`}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Required Section</Label>
                <p className="text-xs text-muted-foreground">
                  Must be completed before submission
                </p>
              </div>
              <Switch
                checked={section.required !== false}
                onCheckedChange={(checked) => onUpdate(index, 'required', checked)}
                data-testid={`section-required-switch-${index}`}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Edit Template Page Component
 */
export function EditTemplatePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getTemplate, updateTemplate } = useTemplates();

  const template = getTemplate(id);

  const [formData, setFormData] = useState({
    name: '',
    country: '',
    description: '',
    status: 'ACTIVE',
  });
  const [sections, setSections] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [showAddSection, setShowAddSection] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize form with template data
  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name,
        country: template.country,
        description: template.description || '',
        status: template.status || 'ACTIVE',
      });
      setSections(template.sections || []);
    }
  }, [template]);

  // Handle template not found
  if (!template) {
    return (
      <BaseLayout>
        <div className="flex flex-col items-center justify-center py-20">
          <FolderOpen className="h-16 w-16 text-muted-foreground/50 mb-4" />
          <h2 className="text-xl font-semibold mb-2">Template Not Found</h2>
          <p className="text-muted-foreground mb-4">The template you're trying to edit doesn't exist.</p>
          <Button onClick={() => navigate(ROUTES.TEMPLATES)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Templates
          </Button>
        </div>
      </BaseLayout>
    );
  }

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleAddSection = (sectionData) => {
    setSections((prev) => [
      ...prev,
      { id: `SEC-${Date.now()}`, ...sectionData, order: prev.length + 1 },
    ]);
    setShowAddSection(false);
    setHasChanges(true);
  };

  const handleUpdateSection = (index, field, value) => {
    setSections((prev) =>
      prev.map((s, i) => (i === index ? { ...s, [field]: value } : s))
    );
    setHasChanges(true);
  };

  const handleRemoveSection = (index) => {
    setSections((prev) => prev.filter((_, i) => i !== index));
    setHasChanges(true);
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Template name is required';
    }

    if (!formData.country) {
      newErrors.country = 'Please select a country';
    }

    if (sections.length === 0) {
      newErrors.sections = 'At least one section is required';
    } else {
      const hasEmptyTitles = sections.some((s) => !s.title?.trim());
      if (hasEmptyTitles) {
        newErrors.sections = 'All sections must have a title';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      toast.error('Please fix the errors before saving');
      return;
    }

    setIsSaving(true);

    const result = updateTemplate(template.id, {
      name: formData.name.trim(),
      country: formData.country,
      description: formData.description.trim(),
      status: formData.status,
      sections: sections.map((s, i) => ({
        ...s,
        order: i + 1,
        required: s.required !== false,
      })),
    });

    setIsSaving(false);

    if (result.success) {
      toast.success('Template updated successfully');
      setHasChanges(false);
      navigate(`/admin/templates/${template.id}`);
    } else {
      toast.error(result.error || 'Failed to update template');
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        navigate(`/admin/templates/${template.id}`);
      }
    } else {
      navigate(`/admin/templates/${template.id}`);
    }
  };

  return (
    <BaseLayout>
      <div className="space-y-6" data-testid="edit-template-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleCancel}
            data-testid="back-button"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">Edit Template</h1>
            <p className="text-muted-foreground">
              Modify "{template.name}"
            </p>
          </div>
          {hasChanges && (
            <Badge variant="outline" className="text-amber-600 border-amber-300">
              Unsaved Changes
            </Badge>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Left Column - Template Details */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Template Details</CardTitle>
                  <CardDescription>
                    Basic information about the template
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Template Name */}
                  <div className="space-y-2">
                    <Label htmlFor="template-name">Template Name *</Label>
                    <Input
                      id="template-name"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      className={errors.name ? 'border-red-500' : ''}
                      data-testid="template-name-input"
                    />
                    {errors.name && (
                      <p className="text-xs text-red-500">{errors.name}</p>
                    )}
                  </div>

                  {/* Country */}
                  <div className="space-y-2">
                    <Label>Country/Region *</Label>
                    <Select
                      value={formData.country}
                      onValueChange={(value) => handleChange('country', value)}
                    >
                      <SelectTrigger
                        className={errors.country ? 'border-red-500' : ''}
                        data-testid="template-country-select"
                      >
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRIES.map((country) => (
                          <SelectItem key={country.code} value={country.code}>
                            {country.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.country && (
                      <p className="text-xs text-red-500">{errors.country}</p>
                    )}
                  </div>

                  {/* Description */}
                  <div className="space-y-2">
                    <Label htmlFor="template-description">Description</Label>
                    <Textarea
                      id="template-description"
                      value={formData.description}
                      onChange={(e) => handleChange('description', e.target.value)}
                      rows={4}
                      data-testid="template-description-input"
                    />
                  </div>

                  {/* Status */}
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(value) => handleChange('status', value)}
                    >
                      <SelectTrigger data-testid="template-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ACTIVE">Active</SelectItem>
                        <SelectItem value="DRAFT">Draft</SelectItem>
                        <SelectItem value="ARCHIVED">Archived</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              {/* Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Total Sections</span>
                      <span className="font-medium">{sections.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Required</span>
                      <span className="font-medium">
                        {sections.filter((s) => s.required !== false).length}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Optional</span>
                      <span className="font-medium">
                        {sections.filter((s) => s.required === false).length}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Sections */}
            <div className="lg:col-span-2">
              <Card className="h-full">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Sections</CardTitle>
                      <CardDescription>
                        Click on a section to expand and edit
                      </CardDescription>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowAddSection(true)}
                      data-testid="add-section-btn"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Section
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {sections.length > 0 ? (
                    <ScrollArea className="h-[500px] pr-4">
                      <div className="space-y-3">
                        {sections.map((section, index) => (
                          <EditableSectionItem
                            key={section.id}
                            section={section}
                            index={index}
                            onRemove={handleRemoveSection}
                            onUpdate={handleUpdateSection}
                          />
                        ))}
                      </div>
                    </ScrollArea>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-center border-2 border-dashed rounded-lg">
                      <FolderOpen className="h-12 w-12 text-muted-foreground/50 mb-4" />
                      <p className="text-muted-foreground mb-2">No sections defined</p>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowAddSection(true)}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add First Section
                      </Button>
                    </div>
                  )}

                  {errors.sections && (
                    <p className="text-sm text-red-500 text-center mt-4">{errors.sections}</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-4 mt-6 pt-6 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isSaving}
              data-testid="cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSaving || !hasChanges}
              data-testid="save-template-btn"
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </form>

        {/* Add Section Modal */}
        <AddSectionModal
          open={showAddSection}
          onOpenChange={setShowAddSection}
          onSubmit={handleAddSection}
          existingSections={sections}
        />
      </div>
    </BaseLayout>
  );
}

export default EditTemplatePage;
